import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, forwardRef } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AwareModel } from '@appbolaget/aware-model';
import { MatAutocompleteSelectedEvent, MatAutocompleteModule } from '@angular/material/autocomplete';
import { AwareHttpRequest, IAwareCollection } from '@appbolaget/aware-http';
import { Observable, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { NgFor, NgIf, AsyncPipe } from '@angular/common';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { SecureImagePipe } from '@helpers/pipes';

export type ModelWithTitleAndImage = AwareModel & {
    title?: string;
    name?: string;
    image?: string;
};

@Component({
    selector: 'app-chips-autocomplete',
    templateUrl: 'chips-autocomplete.component.html',
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
            }

            ::ng-deep .mdc-evolution-chip__action:before {
                border-color: transparent !important;
            }
        `,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => ChipsAutocompleteComponent),
        },
    ],
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatChipsModule,
        NgFor,
        NgIf,
        MatIconModule,
        FormsModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        MatOptionModule,
        AsyncPipe,
        SecureImagePipe,
    ],
})
export class ChipsAutocompleteComponent implements ControlValueAccessor, OnInit, OnDestroy {
    @Input() label: string;
    @Input() placeholder = 'Sök...';
    @Input() request: AwareHttpRequest<IAwareCollection<ModelWithTitleAndImage>>;
    @Input() optionLabelPredicate?: (option: ModelWithTitleAndImage) => string;
    @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

    ALIVE: boolean;
    chips: ModelWithTitleAndImage[] = [];
    displayProperty: 'title' | 'name' = 'title';
    searchInputControl: FormControl<string> = new FormControl();
    separatorKeysCodes: number[] = [ENTER, COMMA];
    suggestions$: Observable<ModelWithTitleAndImage[]>;

    constructor() {}

    onChange = (_) => {};

    onTouched = () => {};

    touched = false;
    disabled = false;

    ngOnInit(): void {
        this.ALIVE = true;

        this._initListeners();
    }

    ngOnDestroy(): void {
        this.ALIVE = false;
    }

    removeChip(chip: ModelWithTitleAndImage): void {
        this.markAsTouched();
        this.chips = this.chips.filter((c) => c.uuid !== chip.uuid);
        this.onChange(this.chips);
    }

    selectedFromAutocomplete(event: MatAutocompleteSelectedEvent): void {
        this.markAsTouched();
        this.searchInput.nativeElement.value = '';
        this.searchInputControl.setValue(null);

        this.chips = [...this.chips, event.option.value];
        this.onChange(this.chips);
    }

    writeValue(value: ModelWithTitleAndImage[]): void {
        this.chips = value ?? [];
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;

        if (this.disabled) {
            this.searchInputControl.disable({ emitEvent: false });
        } else {
            this.searchInputControl.enable({ emitEvent: false });
        }
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    private _initListeners() {
        this.suggestions$ = this.searchInputControl.valueChanges.pipe(
            distinctUntilChanged(),
            debounceTime(300),
            switchMap((searchTerm) => this._search(searchTerm)),
            tap(this.markAsTouched.bind(this)),
            map((suggestions) => suggestions.filter((suggestion) => !this.chips.find((chip) => chip.uuid === suggestion.uuid))),
        );
    }

    private _search(query: string): Observable<ModelWithTitleAndImage[]> {
        return this.request
            .parameter('query', query)
            .execute()
            .pipe(map((response) => response.data));
    }
}
